<script setup lang="ts">
import { hexToRgba } from "~/telestrations/helpers/hexToRgba";

const props = defineProps<{
  modelValue: number;
  min?: number;
  max?: number;
  step?: number;
  color?: string; // hex
  disabled?: boolean;
}>();

const emit = defineEmits<(e: "update:modelValue", event: Event) => void>();

function onInput(e: Event) {
  emit("update:modelValue", e);
}

const colorMode = useColorMode();
const isDark = computed(() => colorMode.value === "dark");

const opacityGradient = computed(() => {
  if (!props.color || props.disabled) {
    const color = isDark.value ? "86, 95, 111" : "199, 203, 208";
    return `linear-gradient(90deg, rgba(${color}, 0) 0%, rgba(${color}, 1) 100%)`;
  }

  return `linear-gradient(90deg, ${hexToRgba(props.color, 0)} 0%, ${hexToRgba(props.color, 1)} 100%)`;
});
</script>

<template>
  <div class="opacity-range-container">
    <div class="pointer-events-none absolute inset-0" :style="{ background: opacityGradient }" />
    <input
      type="range"
      :min="props.min ?? 0"
      :max="props.max ?? 100"
      :step="props.step ?? 1"
      :value="props.modelValue"
      :disabled="props.disabled"
      class="u-opacity-range"
      @input="onInput"
    >
  </div>
</template>

<style scoped>
.opacity-range-container {
  position: relative;
  background: url('data:image/svg+xml;utf8,<svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="8" height="8" fill="%23F8F8F8"/><rect x="4" width="4" height="4" fill="%23AFB6C1"/><rect y="4" width="4" height="4" fill="%23AFB6C1"/></svg>')
    repeat;
  border-radius: 100px;
  overflow: hidden;
}
.dark .opacity-range-container {
  background: url("data:image/svg+xml,%3Csvg width='8' height='12' viewBox='0 0 8 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M4 16H0V12H4V16ZM12.001 16H8.00098V12H12.001V16ZM20 16H16V12H20V16ZM4 7.99902H8V11.999H4V8.00098H0V4.00098H4V7.99902ZM12.001 7.99902H15.999V11.999H11.999V8.00098H8.00098V4.00098H12.001V7.99902ZM20 8.00098H16V4.00098H20V8.00098ZM8 4H4V0H8V4ZM12.001 0H15.999V4H11.999V0H8.00098V-4H12.001V0ZM4 0H0V-4H4V0ZM20 0H16V-4H20V0Z' fill='%23A9B7D1'/%3E%3C/svg%3E")
    repeat;
}
.u-opacity-range {
  position: relative;
  z-index: 2;
  width: 100%;
  background: transparent !important;
  -webkit-appearance: none;
  appearance: none;
}
.u-opacity-range::-webkit-slider-thumb {
  appearance: none;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: radial-gradient(circle 6px at 50% 50%, transparent 50%, #fff 51%);
  cursor: pointer;
  border: none;
  box-shadow: 0px 1px 2px 0px #051b443d;
}
.u-opacity-range::-webkit-slider-thumb:focus {
  outline: none;
}
.u-opacity-range::-webkit-slider-thumb:active {
  outline: none;
}
.u-opacity-range::-moz-range-thumb {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: radial-gradient(circle 6px at 50% 50%, transparent 50%, #fff 51%);
  cursor: pointer;
  box-shadow: 0px 1px 2px 0px #051b443d;
  transform: translateY(-3px);
  border: none;
}
.u-opacity-range::-moz-range-thumb:focus {
  outline: none;
}
.u-opacity-range::-moz-range-thumb:active {
  outline: none;
}
.u-opacity-range::-ms-thumb {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: radial-gradient(circle 6px at 50% 50%, transparent 50%, #fff 51%);
  cursor: pointer;
  border: none;
  box-shadow: 0px 1px 2px 0px #051b443d;
}
.u-opacity-range::-ms-thumb:focus {
  outline: none;
}
.u-opacity-range::-ms-thumb:active {
  outline: none;
}
</style>
